import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { getAppConfig } from '../services/appConfigService';

const AppConfigContext = createContext(null);

export const useAppConfig = () => {
  const ctx = useContext(AppConfigContext);
  if (!ctx) throw new Error('useAppConfig must be used within AppConfigProvider');
  return ctx;
};

const REFRESH_INTERVAL_MS = 60_000;

export const AppConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const refresh = useCallback(async () => {
    const next = await getAppConfig();
    setConfig(next);
    setLoading(false);
    return next;
  }, []);

  useEffect(() => {
    refresh();

    // Refresh whenever the tab/app comes back to foreground so admin toggles
    // take effect quickly without a redeploy.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);

    intervalRef.current = setInterval(refresh, REFRESH_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refresh]);

  return (
    <AppConfigContext.Provider value={{ config, loading, refresh }}>
      {children}
    </AppConfigContext.Provider>
  );
};
